import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export type ExportFormat = 'csv' | 'excel' | 'pdf';

export interface ExportData {
  headers: string[];
  rows: any[][];
  title: string;
}

export function exportToCSV(data: ExportData): void {
  const csvContent = [
    data.headers.join(','),
    ...data.rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${data.title}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportToExcel(data: ExportData): void {
  const worksheet = XLSX.utils.aoa_to_sheet([data.headers, ...data.rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, data.title);
  
  XLSX.writeFile(workbook, `${data.title}_${new Date().toISOString().split('T')[0]}.xlsx`);
}

export function exportToPDF(data: ExportData): void {
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(16);
  doc.text(data.title, 14, 20);
  
  // Add date
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);
  
  // Add table
  autoTable(doc, {
    head: [data.headers],
    body: data.rows,
    startY: 35,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [59, 130, 246] },
  });
  
  doc.save(`${data.title}_${new Date().toISOString().split('T')[0]}.pdf`);
}

export async function logExport(
  userId: string,
  exportType: string,
  fileName: string,
  format: ExportFormat,
  recordCount: number,
  fileSize: number
): Promise<void> {
  const { supabase } = await import("@/integrations/supabase/client");
  
  await supabase.from("export_history" as any).insert({
    user_id: userId,
    export_type: exportType,
    file_name: fileName,
    file_format: format,
    record_count: recordCount,
    file_size: fileSize,
    status: "completed",
  });
}

export function exportData(data: ExportData, format: ExportFormat): void {
  const recordCount = data.rows.length;
  
  switch (format) {
    case 'csv':
      exportToCSV(data);
      break;
    case 'excel':
      exportToExcel(data);
      break;
    case 'pdf':
      exportToPDF(data);
      break;
  }

  // Log export (fire and forget)
  if (typeof window !== 'undefined') {
    import("@/integrations/supabase/client").then(({ supabase }) => {
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) {
          const fileName = `${data.title}_${new Date().toISOString().split('T')[0]}.${format}`;
          logExport(user.id, data.title, fileName, format, recordCount, 0);
        }
      });
    });
  }
}
